import { HttpResponse } from "@angular/common/http";
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit } from "@angular/core";
import { List } from "immutable";
import { Observable, timer } from "rxjs";
import { first, takeWhile } from "rxjs/operators";
import { SubSink } from "subsink";
import { MessagingService } from "../../../../core/messaging/messaging.service";
import { SeverityType } from "../../../../core/messaging/severity-type.enum";
import { ListItem } from "../../../../shared/list/list-item";
import { DateHelper } from "../../../../utilities/contracts/date-helper";
import { NumberHelper } from "../../../../utilities/contracts/number-helper";
import { StringHelper } from "../../../../utilities/contracts/string-helper";
import { IS_VENDOR_MATCHING_COMPLETED, VENDOR_MATCHING_REQUIRED } from "../../member/chase-detail/chase-detail-chart/attributes";
import { ProjectConfigSummary } from "../../project/project-config/models/project-config-summary.model";
import { ProjectConfigurationService } from "../../project/project-config/project-config.service";
import { EmailNotification } from "../data-load-email-notification.model";
import { DataLoadState } from "../data-load-state.model";
import { DataLoadService } from "../data-load.service";
import { VendorMatchingStatus } from "../vendor-matching-status.enum";

@Component({
  selector: "app-data-load-summary",
  templateUrl: "./summary.component.html",
  styleUrls: ["./summary.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SummaryComponent implements OnInit, OnDestroy {
  private sink = new SubSink();
  dataLoadId: number;
  dataLoadState: DataLoadState;
  isProcessingVisible = false;
  isLoadComplete = false;
  summaryReport = List<ListItem>();
  isUploadSuccess = false;
  dataLoadOption: string;
  expandedError = false;
  expandedWarning = false;
  errorSummaryReport: any;
  warningSummaryReport: any;
  isVendorMatchingRequired = false;
  vendorMatchingStatusCode = "";
  projectAttributes = null;
  isValidationComplete = false;
  chaseSummaryReport: { key: string; value: number }[] = [];
  addressSummaryReport: { key: string; value: number }[] = [];
  totalChases = 0;
  expandedChase = false;
  expandedAddress = false;
  disableValidationSummaryBtn = false;

  constructor(
    private readonly changeDetector: ChangeDetectorRef,
    private dataLoadService: DataLoadService,
    private readonly messagingService: MessagingService,
    private projectConfigurationService: ProjectConfigurationService
  ) { }

  ngOnInit() {
    this.sink.add(
      this.dataLoadService.state.subscribe(state => {
        this.dataLoadState = state;
        this.changeDetector.markForCheck();
      })
    );

    this.isValidationComplete = this.dataLoadState.isValidationComplete;
    this.getProjectConfigurations(this.dataLoadState.projectId);
    if (this.currentDataLoadId > 0) {
      this.getSummaryReport(this.currentDataLoadId);
      this.getErrorDisplayReport(this.currentDataLoadId);
    }
    this.changeDetector.markForCheck();
  }

  ngOnDestroy() {
    this.sink.unsubscribe();
  }

  get currentDataLoadId(): number {
    return this.dataLoadState.dataLoadId;
  }

  get isLoadProcess(): boolean {
    return this.dataLoadState.isLoadProcess;
  }

  get isReportLoaded(): boolean {
    return NumberHelper.isGreaterThan(this.summaryReport.size, 0);
  }

  get summaryHeader(): string {
    return this.isLoadProcess ? "IMPORT SUMMARY" : "DATA LOAD SUMMARY";
  }
  get getIsVendorMatchingCompleted(): boolean {
    const result = this.vendorMatchingStatusCode === VendorMatchingStatus.COMPLETED ||
      this.vendorMatchingStatusCode === VendorMatchingStatus.FAILED;

    return result || !this.getIsVendorMatchingRequired;
  }
  get getIsVendorMatchingRequired(): boolean {
    return this.isVendorMatchingRequired && this.dataLoadState.loadType.toLowerCase() !== "adminrefresh";
  }
  get getVendorMatchingButtonText(): string {
    return this.vendorMatchingStatusCode === VendorMatchingStatus.FAILED ? "Try Again" : "Vendor Matching";
  }
  get getVendorMatchingStatus(): string {
    switch (this.vendorMatchingStatusCode) {
      case VendorMatchingStatus.IN_PROCESSES:
        return "In Progress";
      case VendorMatchingStatus.COMPLETED:
        return "Completed";
      case VendorMatchingStatus.FAILED:
        return "Failed";
      default:
        return "Not Processed";
    }
  }
  get validationStatusMessage(): string {
    if (this.isDataLoadNoWarningsEnabled) {
      return "PASSED";
    } else if (this.isDataLoadWithWarningsEnabled) {
      return "PASSED WITH WARNING";
    } else if (this.isPartialDataLoadNoWarningsEnabled || this.isPartialDataLoadWithWarningsEnabled) {
      return "PARTIALLY FAILED";
    } else if (this.isDataLoadDisabled) {
      return "FAILED";
    }
  }

  get totalChasesPassedToLoad(): number {
    return this.isReportLoaded ? Number(this.summaryReport.find(x => x.key === "Total Chases Passed Validation").value) : 0;
  }

  get loadChasesText(): string {
    return this.isPartialDataLoadNoWarningsEnabled ?
      `LOAD ${this.totalChasesPassedToLoad} CHASES WITH WARNINGS`
      : `LOAD ${this.totalChasesPassedToLoad} CHASES WITH WARNINGS`;
  }

  // get dataLoadOption(): string {
  //  return this.isReportLoaded ? this.summaryReport.find(x => x.key.toLowerCase() === "data load option").value : "";
  // }

  get isDataLoadNoWarningsEnabled(): boolean {
    if (!StringHelper.isAvailable(this.dataLoadOption)) {
      return false;
    }

    return this.dataLoadOption.toLowerCase() === "data load enabled";
  }

  get isDataLoadWithWarningsEnabled(): boolean {
    if (!StringHelper.isAvailable(this.dataLoadOption)) {
      return false;
    }

    return this.dataLoadOption.toLowerCase() === "data load enabled with warnings";
  }

  get isPartialDataLoadNoWarningsEnabled(): boolean {
    if (!StringHelper.isAvailable(this.dataLoadOption)) {
      return false;
    }

    return this.dataLoadOption.toLowerCase() === "partial data load enabled";
  }

  get isPartialDataLoadWithWarningsEnabled(): boolean {
    if (!StringHelper.isAvailable(this.dataLoadOption)) {
      return false;
    }

    return this.dataLoadOption.toLowerCase() === "partial data load enabled with warnings";
  }

  get isDataLoadDisabled(): boolean {
    if (!StringHelper.isAvailable(this.dataLoadOption)) {
      return false;
    }

    return this.dataLoadOption.toLowerCase() === "data load disabled";
  }

  get isPartialDataLoadEnabled(): boolean {
    return this.isPartialDataLoadNoWarningsEnabled || this.isPartialDataLoadWithWarningsEnabled;
  }

  get isDataLoadWithIssues(): boolean {
    return this.isDataLoadWithWarningsEnabled
      || this.isPartialDataLoadNoWarningsEnabled
      || this.isPartialDataLoadWithWarningsEnabled
      || this.isDataLoadDisabled;
  }
  get processHeaderText(): string {
    if (!this.getIsVendorMatchingCompleted && this.getIsVendorMatchingRequired) {
      return "PLEASE WAIT...VENDOR MATCHING IS IN PROGRESS";
    } else if (this.getIsVendorMatchingCompleted && this.getIsVendorMatchingRequired && this.isValidationComplete === false) {
      return "PLEASE WAIT...VALIDATION IS IN PROGRESS";
    } else {
      return "DATA UPLOAD IN PROGRESS... YOU MAY LEAVE THIS PAGE BY PRESSING THE ESC BUTTON. YOU WILL BE NOTIFIED WHEN YOUR UPLOAD IS COMPLETE.";
    }
  }

  isChase(item: any): boolean {
    return item?.key === "Total Chases";
  }

  isAddress(item: any): boolean {
    return item?.key === "Total Distinct Locations";
  }

  isError(item: any): boolean {
    return item?.key === "Total Error Count" && item?.value > 0;
  }

  isWarning(item: any): boolean {
    return item?.key === "Total Warning Count" && item?.value > 0;
  }

  showErrorToggle(item: any): boolean {
    return this.isError(item) && this.expandedError;
  }

  showWarningToggle(item: any): boolean {
    return this.isWarning(item) && this.expandedWarning;
  }

  showChaseToggle(item: any): boolean {
    return this.isChase(item) && this.expandedChase;
  }

  showAddressToggle(item: any): boolean {
    return this.isAddress(item) && this.expandedAddress;
  }

  showIcon(item: any): boolean {
    return this.isError(item) || this.isWarning(item);
  }

  getIconName(item: any): string {
    if (this.isChase(item)) { return this.expandedChase ? "angle-down" : "angle-right"; }
    if (this.isAddress(item)) { return this.expandedAddress ? "angle-down" : "angle-right"; }
    if (this.isError(item)) { return this.expandedError ? "angle-down" : "angle-right"; }
    if (this.isWarning(item)) { return this.expandedWarning ? "angle-down" : "angle-right"; }
    return "angle-right";
  }

  assignAndNotify<T>(data: T[]): List<T> {
    this.changeDetector.markForCheck();
    const dataList = List(data);
    return dataList;
  }

  checkProgress(projectId: number, dataLoadId: number): void {
    timer(0, 2000).pipe(
      takeWhile(() => !this.isLoadComplete)
    ).subscribe(() => {
      this.isProcessDone(projectId, dataLoadId);
    });
  }

  startVendorMatching(): void {
    this.isValidationComplete = false;
    this.isProcessingVisible = true;
    this.processVendorMatching(this.dataLoadState.projectId, this.dataLoadState.dataLoadId, this.dataLoadState.loadType);
    this.changeDetector.markForCheck();
  }

  processVendorMatching(projectId: number, dataLoadId: number, dataLoadType: string): void {
    this.dataLoadService
      .processVendorMatching(projectId, dataLoadId, dataLoadType, dataLoadType)
      .subscribe(
        result => {
          if (result) {
            this.isProcessingVisible = true;
            this.messagingService.showToast("Your request is being processed", SeverityType.SUCCESS);
            this.saveProjectConfig();
            this.checkVendorMatchingProgress(projectId, dataLoadId);
          }
        },
        err => {
          this.isProcessingVisible = false;
          this.messagingService.showToast("Unable to process vendor matching", SeverityType.ERROR);
        });
  }
  checkVendorMatchingProgress(projectId, dataLoadId): void {
    timer(10, 2000).pipe(
      takeWhile(() => {
        const result = this.getIsVendorMatchingCompleted;
        if (result === true) {
          this.isProcessingVisible = false;
          this.changeDetector.markForCheck();
        }
        return result === false;
      })
    ).subscribe(() => {
      this.getProjectConfigurationByAttributeId(projectId, IS_VENDOR_MATCHING_COMPLETED.attributeId);
    });
  }

  async getErrorReport(): Promise<void> {
    this.disableValidationSummaryBtn = true;

    this.messagingService.showToast(
      "Downloading is in progress.",
      SeverityType.SUCCESS
    );

    try {
      const reports: Observable<HttpResponse<Blob>>[] = [];

      reports.push(
        this.dataLoadService.getDetailErrorReport(
          this.dataLoadState.dataLoadId,
          this.dataLoadState.projectName
        )
      );

      reports.push(
        this.dataLoadService.getSummaryErrorReport(
          this.dataLoadState.dataLoadId,
          this.dataLoadState.projectName
        )
      );

      if (this.isPartialDataLoadEnabled) {
        reports.push(
          this.dataLoadService.getChaseOmissionFromLoadReport(
            this.dataLoadState.dataLoadId,
            this.dataLoadState.projectName
          )
        );

        if (
          this.dataLoadState.projectType === "HEDIS" ||
          this.dataLoadState.projectType === "RISK"
        ) {
          reports.push(
            this.dataLoadService.getErrorChasesFromLoadReport(
              this.dataLoadState.dataLoadId,
              this.dataLoadState.projectName,
              this.dataLoadState.projectId
            )
          );

          const files = await this.dataLoadService
            .getListOfFilesToLoad(this.dataLoadState.projectId)
            .pipe(first())
            .toPromise();

          if (files.some(x =>
            (x.fileTypeName === "Diagnosis" || x.fileTypeName === "Attribute") &&
            x.networkFileLocation
          )) {
            reports.push(
              this.dataLoadService.getErrorDiagnosisFromLoadReport(
                this.dataLoadState.dataLoadId,
                this.dataLoadState.projectName,
                this.dataLoadState.projectId,
                this.dataLoadState.projectType
              )
            );
          }

          if (files.some(x =>
            x.fileTypeName === "Compliance" && x.networkFileLocation
          )) {
            reports.push(
              this.dataLoadService.getErrorComplianceFromLoadReport(
                this.dataLoadState.dataLoadId,
                this.dataLoadState.projectName,
                this.dataLoadState.projectId
              )
            );
          }
        }
      }

      await this.downloadReports(reports);

    } catch (err) {
      this.messagingService.showToast(
        "One or more files could not be downloaded.",
        SeverityType.ERROR
      );
    } finally {
      this.disableValidationSummaryBtn = false;
      this.changeDetector.detectChanges();
    }
  }


  private async downloadReports(
    reports: Observable<HttpResponse<Blob>>[]
  ): Promise<void> {

    for (const report$ of reports) {
      const response = await report$
        .pipe(first())
        .toPromise();

      if (!response?.body) {
        continue;
      }

      const fileName = this.extractFileName(response);
      this.downloadFile(response.body, fileName);
    }
  }


  private downloadFile(blob: Blob, fileName: string): void {
    const url = URL.createObjectURL(blob);

    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = fileName;
    anchor.click();

    URL.revokeObjectURL(url);
  }

  private extractFileName(response: HttpResponse<Blob>): string {
    const cd = response.headers.get("content-disposition");
    if (!cd) {
      return "download.csv";
    }

    const utf8 = cd.match(/filename\*\s*=\s*UTF-8''([^;]+)/i);
    if (utf8 && utf8[1]) {
      return decodeURIComponent(utf8[1]);
    }

    const ascii = cd.match(/filename\s*=\s*([^;]+)/i);
    if (ascii && ascii[1]) {
      return ascii[1].replace(/"/g, "");
    }

    return "download.csv";
  }

  private buildChaseSummaryReport(summaryReport: List<ListItem>): void {
    let workingReport = summaryReport;

    const take = (label: string): number => {
      const index = workingReport.findIndex(s => s.key === label);

      if (index < 0) {
        return 0;
      }

      const item = workingReport.get(index);
      workingReport = workingReport.delete(index);

      return Number(item?.value || 0);
    };

    const active = take("Total Chases - Active");
    const pended = take("Total Chases - Pended");
    const inactive = take("Total Chases - Inactive");

    this.chaseSummaryReport = [
      { key: "Total Chases - Active", value: active },
      { key: "Total Chases - Pended", value: pended },
      { key: "Total Chases - Inactive", value: inactive },
    ];

    this.totalChases = active + pended + inactive;

    const totalIndex = workingReport.findIndex(
      s => s.key === "Total Chases"
    );

    if (totalIndex >= 0) {
      const existingItem = workingReport.get(totalIndex);

      if (existingItem) {
        const updatedItem = new ListItem({
          key: existingItem.key,
          value: String(this.totalChases),
        });

        workingReport = workingReport.set(totalIndex, updatedItem);
      }
    }

    this.summaryReport = workingReport;
  }


  private buildAddressSummaryReport(summaryReport: List<ListItem>): void {
    let workingReport = summaryReport;

    const take = (label: string): number => {
      const index = workingReport.findIndex(s => s.key === label);

      if (index < 0) { return 0; }

      const item = workingReport.get(index);
      workingReport = workingReport.delete(index);

      return Number(item?.value || 0);
    };

    const newLoc = take("Total Distinct New Locations");
    const existingLoc = take("Total Distinct Existing Locations");

    // Save updated summaryReport back to component
    this.summaryReport = workingReport;

    this.addressSummaryReport = [
      { key: "New Locations", value: newLoc },
      { key: "Existing Locations", value: existingLoc },
    ];
  }


  getSummaryReport(dataLoadId: number): void {
    this.dataLoadService
      .getSummaryReport(dataLoadId)
      .subscribe(result => {
        const summaryReport = result.map(summaryItem => {
          if (summaryItem.key === "Process start date" || summaryItem.key === "Process end date") {
            const formattedDate = DateHelper.formatToLocalTime(summaryItem.value);
            const newItem = new ListItem({ ...summaryItem, value: formattedDate });
            return newItem;
          }
          return summaryItem;
        });

        const summaryReportTemp = this.assignAndNotify(summaryReport);
        const dataLoadOptionItem = summaryReportTemp.find(s => s.key.toLowerCase() === "data load option");
        this.dataLoadOption = dataLoadOptionItem ? dataLoadOptionItem.value : "";
        this.summaryReport = summaryReportTemp.filterNot(s => s.key.toLowerCase() === "data load option").toList();

        this.summaryReport = this.summaryReport.push(
          new ListItem({ key: "Validation Status", value: this.validationStatusMessage, url: "" })
        );
        this.buildChaseSummaryReport(this.summaryReport);
        this.buildAddressSummaryReport(this.summaryReport);
        this.changeDetector.markForCheck();
      });
  }
  getErrorDisplayReport(dataLoadId: number): void {
    this.dataLoadService
      .getSummaryErrorDisplayReport(dataLoadId)
      .subscribe(
        result => {
          this.errorSummaryReport = result.filter(x => x.messageCategory === "Error");
          this.warningSummaryReport = result.filter(x => x.messageCategory === "Warning");
        });
  }

  getValidationMessageClass(value: string): string {
    switch (value) {
      case "FAILED":
      case "PARTIALLY FAILED":
        return "summary-body__details--danger";
        break;
      case "PASSED WITH WARNING":
      case "PASSED":
        return "summary-body__details--pass";
        break;
      default:
        return "summary-body__details";
        break;
    }
  }

  loadFile(projectId: number, dataLoadId: number, dataLoadType: string): void {
    const processMode = this.isPartialDataLoadEnabled ? "Partial Load" : "Load";
    this.dataLoadService
      .loadData(projectId, dataLoadId, dataLoadType, processMode)
      .subscribe(
        result => {
          if (NumberHelper.isGreaterThan(result, 0)) {
            this.messagingService.showToast("Your request is being processed and you will be alerted when the process will be completed", SeverityType.SUCCESS);
            this.checkProgress(projectId, dataLoadId);
          }
        },
        err => {
          this.isLoadComplete = true;
        });
  }

  startDataLoad(): void {
    this.isLoadComplete = false;
    this.dataLoadId = NumberHelper.randomWholeNumber(1000000000, 2000000000);
    this.dataLoadService.setState({
      dataLoadId: this.dataLoadId,
    });
    this.isProcessingVisible = true;
    this.loadFile(this.dataLoadState.projectId, this.dataLoadId, this.dataLoadState.loadType);
  }

  trackByIndex(index, item) {
    return index;
  }

  private isProcessDone(projectId: number, dataLoadId: number): void {
    this.dataLoadService.isProcessDone(projectId, dataLoadId)
      .subscribe(result => {
        this.isLoadComplete = result;
        if (this.isLoadComplete) {
          this.isProcessingVisible = false;
          this.isUploadSuccess = true;
          this.getSummaryReport(this.currentDataLoadId);
          this.sendEmailNotification();
          this.dataLoadService.setState({
            isLoadProcess: !this.isLoadComplete,
          });
        }
        this.changeDetector.markForCheck();
      });
  }

  private sendEmailNotification(): void {
    const emailNotification = new EmailNotification({
      projectId: this.dataLoadState.projectId,
      projectName: this.dataLoadState.projectName,
      loadDate: this.summaryReport.find(x => x.key === "Load Date").value,
      totalChases: parseInt(this.summaryReport.find(x => x.key === "Total Chase Records")?.value, 10),
    });

    this.dataLoadService
      .sendEmailNotification(emailNotification)
      .subscribe();
  }
  toggleSection(key: string) {
    if (key === "Total Error Count") {
      this.expandedError = !this.expandedError;
    } else if (key === "Total Warning Count") {
      this.expandedWarning = !this.expandedWarning;
    }
  }
  private getProjectConfigurationByAttributeId(projectId: number, attributeId: number): void {
    this.projectConfigurationService.getProjectAttribute(projectId, attributeId)
      .subscribe(config => {
        this.vendorMatchingStatusCode = config.attributeValue;
      });
  }
  private getProjectConfigurations(projectId: number): void {
    if (projectId) {
      const attributeIdsAsCsv = `${IS_VENDOR_MATCHING_COMPLETED.attributeId},
      ${VENDOR_MATCHING_REQUIRED.attributeId}`;

      this.projectConfigurationService.getProjectAttributeList(projectId, attributeIdsAsCsv)
        .subscribe(configs => {
          this.projectAttributes = configs;
          this.vendorMatchingStatusCode = configs.find(x => x.attributeId === IS_VENDOR_MATCHING_COMPLETED.attributeId)?.attributeValue;
          this.isVendorMatchingRequired = configs.find(x => x.attributeId === VENDOR_MATCHING_REQUIRED.attributeId)?.attributeValue === "1";
          this.changeDetector.markForCheck();
        });
    }
  }
  private saveProjectConfig(): void {
    const projectConfigurationSummary = this.buildSingleProjectConfigurationSummaryModel("IsVendorMatchingCompleted");
    if (projectConfigurationSummary != null) {
      this.projectConfigurationService.saveProjectAttributes(projectConfigurationSummary, this.dataLoadState.projectName, undefined).subscribe({
        error: err => {
          if (err.error instanceof Error) {
            this.messagingService.showToast("A client side error occurred while saving.", SeverityType.ERROR);
          } else {
            this.messagingService.showToast("A back end error occurred while saving project configurations.", SeverityType.ERROR);
          }
        },
      });
    }
  }
  private buildSingleProjectConfigurationSummaryModel(attributeKey: string) {
    const matchingAttribute = this.projectAttributes.find(
      attr => attr.attributeCode === attributeKey
    );
    if (matchingAttribute) {
      matchingAttribute.attributeValue = VendorMatchingStatus.IN_PROCESSES;
      const model = new ProjectConfigSummary({
        projectId: this.dataLoadState.projectId,
        projectConfigurationAttributes: matchingAttribute ? [matchingAttribute] : [],
      });
      return model;
    }

    return null;
  }

  startDataValidation(): void {

    this.dataLoadId = NumberHelper.randomWholeNumber(1000000000, 2000000000);
    this.dataLoadService.setState({
      dataLoadId: this.dataLoadId,
    });
    this.isValidationComplete = false; // reset report
    this.isProcessingVisible = true;
    this.validateData(this.dataLoadState.projectId, this.dataLoadId, this.dataLoadState.loadType);
    this.changeDetector.markForCheck();

  }


  validateData(projectId: number, dataLoadId: number, dataLoadType: string): void {
    this.isProcessingVisible = true;
    this.dataLoadService
      .validateData(projectId, dataLoadId, dataLoadType)
      .subscribe(
        result => {
          if (NumberHelper.isGreaterThan(result, 0)) {
            this.checkValidationProgress(projectId, dataLoadId);
          }
        },
        err => {
          this.isValidationComplete = true;
          this.isProcessingVisible = false;
        });
  }
  checkValidationProgress(projectId, dataLoadId): void {
    timer(0, 2000).pipe(
      takeWhile(() => !this.isValidationComplete)
    ).subscribe(() => {
      this.isValidationProcessDone(projectId, dataLoadId);
    });
    this.changeDetector.markForCheck();
  }
  private isValidationProcessDone(projectId: number, dataLoadId: number): void {
    this.dataLoadService.isProcessDone(projectId, dataLoadId)
      .subscribe(result => {
        this.isValidationComplete = result;
        if (this.isValidationComplete) {
          this.isProcessingVisible = false;
          this.getErrorDisplayReport(dataLoadId);
          this.getSummaryReport(dataLoadId);
        }
        this.changeDetector.markForCheck();
      });
  }
}
